import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { conditionalArray } from '../../helpers/array';
import { useAppContext } from '../../pages/App/AppContextProvider';
import CreateMultiAutocomplete from '../CreateMultiAutocomplete';
import messages from './messages';
import MultiAutocompleteOption from './MultiAutocompleteOption';
import UsersForm from './UsersForm';

function UsersAutocomplete({ hasFormItemKey, isDirty, newItemId, newItemWithoutLoginId, optionsData, scrollableTags }) {
  const { watch } = useFormContext();
  const { formatMessage } = useIntl();
  const { isUserLoggedAsOrySso } = useAppContext();

  const options = useMemo(
    () =>
      conditionalArray([
        !isUserLoggedAsOrySso && {
          id: newItemId,
          label: formatMessage(messages.usersAutocompleteNewItemLabel),
          tag: formatMessage(messages.usersAutocompleteNewItemShortLabel),
        },
        !isUserLoggedAsOrySso && {
          id: newItemWithoutLoginId,
          label: formatMessage(messages.usersAutocompleteNewItemWithoutLoginLabel),
          tag: formatMessage(messages.usersAutocompleteNewItemWithoutLoginShortLabel),
        },
        ...optionsData,
      ]),
    [formatMessage, isUserLoggedAsOrySso, newItemId, newItemWithoutLoginId, optionsData]
  );

  return (
    <CreateMultiAutocomplete
      scrollableTags={scrollableTags}
      createForm={watch(hasFormItemKey) && <UsersForm />}
      createFormVisibilityKey={hasFormItemKey}
      customOption={(renderProps, option) => (
        <MultiAutocompleteOption
          key={option.id}
          newItemId={newItemId}
          newItemWithoutLoginId={newItemWithoutLoginId}
          option={option}
          optionLabelKey="label"
          renderProps={renderProps}
        />
      )}
      helperText={formatMessage(messages.usersAutocompleteHelperText)}
      label={formatMessage(messages.usersAutocompleteLabel)}
      name="users"
      newItemId={newItemId}
      newItemId2={newItemWithoutLoginId}
      optionLabelKey="label"
      options={options}
      placeholder={!isDirty ? formatMessage(messages.usersAutocompletePlaceholder) : ''}
    />
  );
}

UsersAutocomplete.propTypes = {
  hasFormItemKey: PropTypes.string.isRequired,
  isDirty: PropTypes.number.isRequired,
  newItemId: PropTypes.string.isRequired,
  newItemWithoutLoginId: PropTypes.string.isRequired,
  optionsData: PropTypes.arrayOf(
    PropTypes.shape({
      email: PropTypes.string,
      firstName: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      label: PropTypes.string,
      lastName: PropTypes.string,
    })
  ).isRequired,
  scrollableTags: PropTypes.bool,
};

export default UsersAutocomplete;
